export interface WelcomeTourFeature {
  label: string;
  description: string;
}

export interface WelcomeTourHomeFlags {
  hasBilling: boolean;
  hasGateway: boolean;
  hasAlarmModuleAccess: boolean;
  suppliers?: readonly { id: number }[] | null;
}

export type WelcomeTourStepKind = 'welcome' | 'feature' | 'menu';

export interface WelcomeTourStep {
  kind: WelcomeTourStepKind;
  targetLabel: string | null;
  title: string;
  text: string;
}

const WELCOME_TITLE =
  'Te damos la bienvenida a la nueva Oficina Virtual de propietarios e inquilinos de Gomez Group Metering';
const WELCOME_TEXT =
  'Esta es la nueva versión de la Oficina Virtual de propietarios e inquilinos. Desde aquí podrás consultar tus lecturas y consumos, descargar tus liquidaciones y enviar tus consultas con Gomez Group Metering.';
const FEATURE_TITLE = 'Estas son las funcionalidades que tienes disponibles';
const MENU_TITLE = 'El menú es el punto de partida';
const MENU_TEXT_WITH_FEATURES =
  'En el menú lateral están Inicio, Mis propiedades y Ajustes, y el resto aparece con el mismo nombre que se acaba de señalar.';
const MENU_TEXT_PLAIN = 'En el menú están Inicio, Mis propiedades y Ajustes.';

export function welcomeTourFeatures(
  home: WelcomeTourHomeFlags,
  isGGM: boolean,
  sendRequestsEnabled: boolean,
): WelcomeTourFeature[] {
  const features: WelcomeTourFeature[] = [];
  const hasBilling = home.hasBilling === true;

  if (hasBilling) {
    features.push({
      label: 'Mis liquidaciones',
      description: 'Consulta y descarga tus liquidaciones.',
    });
    features.push({
      label: 'Mis consumos',
      description: 'Revisa el consumo de tu vivienda.',
    });
  }

  if (home.hasGateway === true) {
    features.push({
      label: 'Lecturas diarias',
      description: 'Sigue las lecturas del concentrador.',
    });
  }

  if (hasBilling) {
    features.push({
      label: 'Enviar lectura',
      description: 'Envía la lectura de tu contador.',
    });
  }

  const hasWaterSupplier = home.suppliers?.some((supplier) => supplier.id === 1) === true;
  if (isGGM && hasBilling && hasWaterSupplier) {
    features.push({
      label: 'Simula tu liquidación',
      description: 'Calcula una liquidación antes de que se emita.',
    });
  }

  if (home.hasAlarmModuleAccess === true) {
    features.push({
      label: 'Alarmas',
      description: 'Consulta las alarmas de tu instalación.',
    });
  }

  if (sendRequestsEnabled) {
    features.push({
      label: 'Enviar consulta',
      description: 'Ponte en contacto con Gomez Group Metering.',
    });
  }

  return features;
}

export function welcomeTourSteps(features: readonly WelcomeTourFeature[]): WelcomeTourStep[] {
  return [
    { kind: 'welcome', targetLabel: null, title: WELCOME_TITLE, text: WELCOME_TEXT },
    ...features.map((feature) => ({
      kind: 'feature' as const,
      targetLabel: feature.label,
      title: FEATURE_TITLE,
      text: feature.description,
    })),
    {
      kind: 'menu' as const,
      targetLabel: null,
      title: MENU_TITLE,
      text: features.length > 0 ? MENU_TEXT_WITH_FEATURES : MENU_TEXT_PLAIN,
    },
  ];
}

export function welcomeTourStorageKey(userId: number): string {
  return `ggm_ov_welcome_tour_${userId}`;
}

export function readWelcomeTourUserId(): number | null {
  try {
    const raw = sessionStorage.getItem('ggm-user');
    if (!raw) {
      return null;
    }
    const parsed = JSON.parse(raw) as { userId?: number };
    return typeof parsed.userId === 'number' ? parsed.userId : null;
  } catch {
    return null;
  }
}

export function hasSeenWelcomeTour(): boolean {
  const userId = readWelcomeTourUserId();
  if (userId == null) {
    return true;
  }
  try {
    return localStorage.getItem(welcomeTourStorageKey(userId)) != null;
  } catch {
    return true;
  }
}

export function markWelcomeTourSeen(): void {
  const userId = readWelcomeTourUserId();
  if (userId == null) {
    return;
  }
  try {
    localStorage.setItem(welcomeTourStorageKey(userId), '1');
  } catch {
    // localStorage no disponible
  }
}
